import React, { useState } from 'react'
import t from '../../service/translate.service'
import { observer, inject } from 'mobx-react'
import PropTypes from 'prop-types'
import { useMsal } from '@azure/msal-react'
import LoadingPageOverlay from '../LoadingPageOverlay'
import { useNavigate } from 'react-router'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import PrimaryTitle from '../PrimaryTitle'
import { api } from '../../service/api.service'
import { appFeatureEnabled } from '../../service/appFeatures.service'
import './index.css'

const STEP_CONTRACT_SIGN = 'contractSign'
const STEP_WELCOME_LETTER = 'welcomeLetter'

const ContractSign = (props) => {
  const { instance } = useMsal()
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [step, setStep] = useState(STEP_CONTRACT_SIGN)
  const [welcomeLetterChoice, setWelcomeLetterChoice] = useState(null)
  const [showWelcomeLetterError, setShowWelcomeLetterError] = useState(false)
  const [welcomeLetterStepCompleted, setWelcomeLetterStepCompleted] = useState(false)
  const [completeLoading, setCompleteLoading] = useState(false)
  const [initialStepSet, setInitialStepSet] = useState(false)

  const user = props?.userStore?.user
  const welcomeLetterEnabled = appFeatureEnabled('welcomeLetterEnabled', user?.contracts || [])

  const getCurrentTenant = () => {
    const scriveSignId = user?.scriveSignId || ''
    return user?.contractAssignedData?.tenantsDetails?.find(
      (tenant) => tenant?.scriveLink?.includes(scriveSignId)
    )
  }

  const hasSavedWelcomeLetterYes = () => getCurrentTenant()?.welcomeLetter === 'YES'

  useEffect(() => {
    if (loading || !user || initialStepSet) {
      return
    }

    if (!welcomeLetterEnabled) {
      setStep(STEP_CONTRACT_SIGN)
      setInitialStepSet(true)
      return
    }

    setStep(
      hasSavedWelcomeLetterYes()
        ? STEP_CONTRACT_SIGN
        : STEP_WELCOME_LETTER
    )
    setInitialStepSet(true)
  }, [loading, user, welcomeLetterEnabled, initialStepSet])

  useEffect(() => {
    if (step === STEP_WELCOME_LETTER && !welcomeLetterEnabled) {
      setStep(STEP_CONTRACT_SIGN)
    }
  }, [step, welcomeLetterEnabled])

  useEffect(() => {
    ;(async () => {
      setTimeout(async () => {
        await props.userStore.loadUser()
        setLoading(false)
      }, 3000)
    })()
  }, [])

  useEffect(() => {
    if (user) {
      const currentTenant = getCurrentTenant()
      if (currentTenant) {
        const sameUsers =
          user?.contractAssignedData?.tenantsDetails?.filter(
            (tenant) =>
              tenant?.email?.toLowerCase() === currentTenant?.email?.toLowerCase()
          )
        setUsers(sameUsers)
      } else {
        setUsers([])
      }
    }
  }, [user])

  useEffect(() => {
    if (step !== STEP_WELCOME_LETTER || welcomeLetterChoice !== null) {
      return
    }

    const currentTenant = getCurrentTenant()
    if (currentTenant?.welcomeLetter === 'YES') {
      setWelcomeLetterChoice(true)
    } else if (currentTenant?.welcomeLetter === 'NO') {
      setWelcomeLetterChoice(false)
    }
  }, [step, user, welcomeLetterChoice])

  const showWelcomeLetterBack = welcomeLetterEnabled && welcomeLetterStepCompleted

  const handleDismissToHome = () => {
    localStorage.setItem('hide_contract_assign_modal', 'true')
    props?.userStore?.setShowContractSignModal()
    navigate('/')
  }

  const handleWelcomeLetterChoice = (value) => {
    setWelcomeLetterChoice(value)
    setShowWelcomeLetterError(false)
  }

  const handleWelcomeLetterContinue = async () => {
    if (welcomeLetterChoice === null) {
      setShowWelcomeLetterError(true)
      return
    }
    setCompleteLoading(true)
    const contractNumber = user?.contractAssignedData?.contractNumber
    if (contractNumber) {
      try {
        await api.post(`/contract/${contractNumber.replace('/', '-')}/welcome-letter`, {
          scriveSignId: user?.scriveSignId,
          contractAssignedData: user?.contractAssignedData,
          letterViaApp: welcomeLetterChoice === true
        })
      } catch (err) {
        console.error('Welcome letter submit error', err)
      }
    }
    setCompleteLoading(false)
    setWelcomeLetterStepCompleted(true)
    setStep(STEP_CONTRACT_SIGN)
  }

  if (loading || completeLoading || (user && !initialStepSet)) {
    return <LoadingPageOverlay />
  }

  if (welcomeLetterEnabled && step === STEP_WELCOME_LETTER) {
    return (
      <div
        className="contract-sign-page"
        style={{ left: 0 }}
        role="dialog"
        aria-labelledby="welcome-letter-title"
        aria-describedby="welcome-letter-description"
      >
        <div className="wrapper">
          <PrimaryTitle id="welcome-letter-title" title={t.CONTRACT_SIGN_WELCOME_LETTER_STEP_TITLE} />
          <div id="welcome-letter-description" className="description contract-sign-welcome-letter-text">
            <p className="contract-sign-welcome-letter-question">{t.CONTRACT_SIGN_WELCOME_LETTER_APP_BENEFIT}</p>
            <p className="contract-sign-welcome-letter-question">{t.CONTRACT_SIGN_WELCOME_LETTER_ALL_PARTIES}</p>
            <p className="contract-sign-welcome-letter-question">{t.CONTRACT_SIGN_WELCOME_LETTER_QUESTION}</p>
            <p className="contract-sign-welcome-letter-question contract-sign-welcome-letter-change-later">{t.CONTRACT_SIGN_WELCOME_LETTER_CHANGE_LATER}</p>
          </div>
          {showWelcomeLetterError && (
            <p className="contract-sign-welcome-letter-error" role="alert" aria-live="assertive">
              {t.CONTRACT_SIGN_WELCOME_LETTER_SELECT_REQUIRED}
            </p>
          )}
          <div className="contract-sign-welcome-letter-buttons" role="group" aria-label={t.CONTRACT_SIGN_WELCOME_LETTER_QUESTION}>
            <button
              type="button"
              className={welcomeLetterChoice === true ? 'button primary' : 'button secondary'}
              onClick={() => handleWelcomeLetterChoice(true)}
              aria-pressed={welcomeLetterChoice === true}
              aria-label={t.CONTRACT_SIGN_WELCOME_LETTER_YES}
            >
              {t.CONTRACT_SIGN_WELCOME_LETTER_YES}
            </button>
            <button
              type="button"
              className={welcomeLetterChoice === false ? 'button primary' : 'button secondary'}
              onClick={() => handleWelcomeLetterChoice(false)}
              aria-pressed={welcomeLetterChoice === false}
              aria-label={t.CONTRACT_SIGN_WELCOME_LETTER_NO}
            >
              {t.CONTRACT_SIGN_WELCOME_LETTER_NO}
            </button>
          </div>
          <div className="contract-sign-welcome-letter-actions">
            {props?.userStore?.haveContractToSign && props?.userStore?.showContractSignModal ? (
              <button
                type="button"
                className="button tertiary"
                onClick={handleDismissToHome}
                aria-label={t.CONTRACT_SIGN_BACK_TO_HOME}
              >
                {t.CONTRACT_SIGN_BACK_TO_HOME}
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="button primary"
              onClick={handleWelcomeLetterContinue}
              disabled={welcomeLetterChoice === null}
              aria-label={t.CONTRACT_SIGN_WELCOME_LETTER_CONTINUE}
            >
              {t.CONTRACT_SIGN_WELCOME_LETTER_CONTINUE}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`contract-sign-page ${props?.userStore?.haveContractToSign && props?.userStore?.showContractSignModal ? 'thank-you-overlay' : ''}`}
      style={{ left: 0 }}
      role="dialog"
      aria-labelledby="thank-you-title"
      aria-describedby="thank-you-description"
    >
      <div className="wrapper">
        <PrimaryTitle title={t.CONTRACT_SIGN} />
        <div
          role="alert"
          aria-live="assertive"
          id="thank-you-description"
          className="description"
        >
          {user?.contractAssignedData?.tenantsDetails?.length > 1
            ? t.CONTRACT_SIGN_MULTI_THANK_YOU_TEXT
            : t.CONTRACT_SIGN_THANK_YOU_TEXT}
        </div>
        <div className={`contract-sign-actions ${showWelcomeLetterBack ? '' : 'contract-sign-actions--sign-only'}`}>
          {showWelcomeLetterBack && (
            <button
              className="button secondary"
              type="button"
              onClick={() => setStep(STEP_WELCOME_LETTER)}
              aria-label={t.BACK}
            >
              {t.BACK}
            </button>
          )}
          <div className="contract-sign-sign-buttons">
            {users?.map((userItem, index) => (
              <Link
                key={index}
                to={userItem?.scriveLink || ''}
                className="button primary contract-sign-link"
                target="_blank"
                aria-label={t.CONTRACT_SIGN_CONTINUE}
              >
                {users?.length === 1
                  ? t.CONTRACT_SIGN_CONTINUE
                  : `${userItem?.firstName} ${userItem?.lastName}`}
              </Link>
            ))}
          </div>
        </div>
        {props?.userStore?.haveContractToSign && props?.userStore?.showContractSignModal && (
          <button
            style={{ marginTop: '10px' }}
            className="button tertiary"
            type='button'
            onClick={handleDismissToHome}
            aria-label={t.CONTRACT_SIGN_BACK_TO_HOME}
          >
            {t.CONTRACT_SIGN_BACK_TO_HOME}
          </button>
        )}
      </div>
    </div>
  )
}
ContractSign.propTypes = {
  userStore: PropTypes.object.isRequired
}
export default inject('userStore')(observer(ContractSign))
